/**
 * Geteilte Filter-Primitives (F3) — extrahiert aus Portfolio Atlas, damit
 * Phase Board + Staffing denselben Filter-Satz nutzen statt duplizieren.
 * Rein präsentational (kein i18n hier — Labels kommen als props/children).
 */
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

export function ChipRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span
        className="text-[10px] uppercase tracking-[0.14em] w-[120px]"
        style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

export function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-2.5 py-1 rounded-md text-xs transition-colors"
      style={{
        background: active ? 'var(--primary-soft)' : 'var(--surface-2)',
        color: active ? 'var(--primary)' : 'var(--ink-2)',
        border: `1px solid ${active ? 'var(--primary)' : 'var(--line-soft)'}`,
        fontFamily: 'var(--f-mono)',
      }}
    >
      {children}
    </button>
  );
}

export function Toggle({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 transition-colors"
      style={{
        background: active ? 'var(--primary-soft)' : 'var(--surface-2)',
        color: active ? 'var(--primary)' : 'var(--ink-2)',
        border: `1px solid ${active ? 'var(--primary)' : 'var(--line-soft)'}`,
      }}
    >
      <span
        className="w-3 h-3 rounded-sm flex items-center justify-center"
        style={{
          background: active ? 'var(--primary)' : 'transparent',
          border: `1px solid ${active ? 'var(--primary)' : 'var(--ink-3)'}`,
        }}
      >
        {active && (
          <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
            <path d="M5 12l5 5L20 7" />
          </svg>
        )}
      </span>
      {children}
    </button>
  );
}

/** Such-Input mit Lupe — wiederverwendbar in allen Filter-Rails. */
export function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div
      className="flex items-center gap-2 px-3 py-2 rounded-md"
      style={{ background: 'var(--surface-2)', border: '1px solid var(--line-soft)' }}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        style={{ color: 'var(--ink-3)' }}
      >
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-3.5-3.5" />
      </svg>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 bg-transparent outline-none text-sm"
        style={{ color: 'var(--ink)' }}
      />
    </div>
  );
}

export type ChipOption = { value: string; label: string };

/**
 * Einzeilige Single-Select-Filterdimension (Label + "Alle" + Optionen als Chips).
 * `value === null` ⇒ "Alle" aktiv. Genutzt von Atlas/Phase Board/Staffing.
 */
export function ChipSelect({
  label,
  value,
  options,
  onChange,
  allLabel,
}: {
  label: string;
  value: string | null;
  options: ChipOption[];
  onChange: (v: string | null) => void;
  allLabel: string;
}) {
  return (
    <ChipRow label={label}>
      <Chip active={value === null} onClick={() => onChange(null)}>
        {allLabel}
      </Chip>
      {options.map((o) => (
        <Chip key={o.value} active={value === o.value} onClick={() => onChange(o.value)}>
          {o.label}
        </Chip>
      ))}
    </ChipRow>
  );
}

/** Fällig-ab/bis-Bereich (geteilt zwischen Atlas und Phase Board). */
export function DueRangeFilter({
  label,
  fromLabel,
  toLabel,
  from,
  to,
  onFrom,
  onTo,
}: {
  label: string;
  fromLabel: string;
  toLabel: string;
  from: string;
  to: string;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 pt-1">
      <span
        className="text-[10px] uppercase tracking-[0.14em] w-[120px]"
        style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
      >
        {label}
      </span>
      <label
        className="flex items-center gap-1.5 text-[11px]"
        style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
      >
        {fromLabel}
        <input
          type="date"
          value={from}
          onChange={(e) => onFrom(e.target.value)}
          className="text-xs px-2 py-1 rounded outline-none"
          style={{
            background: 'var(--surface-2)',
            color: 'var(--ink)',
            border: '1px solid var(--line-soft)',
          }}
        />
      </label>
      <label
        className="flex items-center gap-1.5 text-[11px]"
        style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
      >
        {toLabel}
        <input
          type="date"
          value={to}
          onChange={(e) => onTo(e.target.value)}
          className="text-xs px-2 py-1 rounded outline-none"
          style={{
            background: 'var(--surface-2)',
            color: 'var(--ink)',
            border: '1px solid var(--line-soft)',
          }}
        />
      </label>
      {(from || to) && (
        <button
          type="button"
          onClick={() => {
            onFrom('');
            onTo('');
          }}
          className="text-[11px] px-2 py-0.5 rounded"
          style={{
            background: 'var(--surface-2)',
            color: 'var(--ink-3)',
            border: '1px solid var(--line-soft)',
          }}
        >
          ✕
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PersonPicker / MultiSelectPicker (#55, umbenannt in #81)
//
// Aus phase-board.tsx hierher gezogen, damit der Staffing-Bereich dieselben
// Picker nutzt statt sie zu duplizieren. Verhalten unveraendert; einzige
// Anpassung: `noMatchLabel` kommt jetzt als Prop, damit diese Datei wie im
// Kopfkommentar zugesichert ohne i18n auskommt.
// ---------------------------------------------------------------------------

/** Eintrag einer Mehrfach-/Einfachauswahl. Traegt keine Personen-Semantik. */
export type MultiSelectOption = { value: string; label: string };
/** Alias fuer `PersonPicker`, der weiterhin ueber Personen spricht. */
export type PersonOption = MultiSelectOption;

/**
 * Sentinel fuer „Projekte ganz ohne Tag" (#81). Steht als normaler Eintrag in
 * derselben Auswahlliste und wirkt additiv wie ein Tag — deshalb ein Wert im
 * `string[]` und keine zweite Zustandsvariable. Kollisionsfrei, weil echte
 * `tag_id`s UUIDs sind.
 */
export const NO_TAGS_VALUE = '__no_tags__';

export function PersonPicker({
  options,
  value,
  onChange,
  label,
  placeholder,
  searchPlaceholder,
  clearLabel,
  noMatchLabel,
}: {
  options: PersonOption[];
  value: string | null;
  onChange: (v: string | null) => void;
  label: string;
  placeholder: string;
  searchPlaceholder: string;
  clearLabel: string;
  noMatchLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const selected = value ? (options.find((o) => o.value === value) ?? null) : null;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 50);
    return options.filter((o) => o.label.toLowerCase().includes(q)).slice(0, 50);
  }, [options, query]);

  if (options.length === 0) return null;

  return (
    <ChipRow label={label}>
      <div ref={ref} className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="px-2.5 py-1 rounded-md text-xs flex items-center gap-1.5 transition-colors"
          style={{
            background: selected ? 'var(--primary-soft)' : 'var(--surface-2)',
            color: selected ? 'var(--primary)' : 'var(--ink-2)',
            border: `1px solid ${selected ? 'var(--primary)' : 'var(--line-soft)'}`,
            fontFamily: 'var(--f-mono)',
          }}
        >
          <span>{selected ? selected.label : placeholder}</span>
          <span style={{ opacity: 0.6 }}>▾</span>
        </button>
        {open && (
          <div
            className="absolute left-0 top-full mt-1 z-20 w-64 rounded shadow-lg"
            style={{ background: 'var(--surface)', border: '1px solid var(--line-soft)' }}
          >
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full text-xs px-2 py-1.5 outline-none rounded-t"
              style={{
                background: 'var(--surface-2)',
                borderBottom: '1px solid var(--line-soft)',
                color: 'var(--ink)',
              }}
            />
            <div className="max-h-60 overflow-y-auto">
              {selected && (
                <button
                  type="button"
                  onClick={() => {
                    onChange(null);
                    setOpen(false);
                    setQuery('');
                  }}
                  className="w-full text-left px-2 py-1.5 text-[11px] border-b"
                  style={{
                    color: 'var(--danger)',
                    borderColor: 'var(--line-soft)',
                    fontFamily: 'var(--f-mono)',
                  }}
                >
                  ✕ {clearLabel}
                </button>
              )}
              {filtered.length === 0 ? (
                <div className="px-2 py-2 text-[11px]" style={{ color: 'var(--ink-3)' }}>
                  {noMatchLabel}
                </div>
              ) : (
                filtered.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => {
                      onChange(o.value);
                      setOpen(false);
                      setQuery('');
                    }}
                    className="w-full text-left px-2 py-1.5 text-[11px] hover:bg-[var(--surface-2)]"
                    style={{ color: o.value === value ? 'var(--primary)' : 'var(--ink-2)' }}
                  >
                    {o.label}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </ChipRow>
  );
}

// Multi-Select-Variante des PersonPicker: mehrere Eintraege gleichzeitig waehlbar
// (Checkbox-Zeilen, Dropdown bleibt beim Anklicken offen).
//
// Hiess bis #81 `MultiPersonPicker` und bediente nur Personenfilter. Seit der
// Tag-Filter dieselbe Bedienung bekommt, waere der Personen-Name eine Luege gewesen —
// und genau so eine Luege verleitet spaeter jemanden dazu, eine tag-spezifische
// Zweitkomponente danebenzustellen. Die Logik ist unveraendert.
//
// WICHTIG: Auswahl laeuft ueber `onToggle(id)`/`onClear()`, die im Parent ein
// FUNKTIONALES State-Update machen (prev => ...). Sonst wuerde bei zuegigen Klicks
// ein zweiter Klick das noch nicht committete `value` des vorherigen Renders
// (Stale-Closure) sehen und die erste Auswahl ersetzen statt ergaenzen — das
// Phase Board rendert bei jeder Filteraenderung komplett neu und ist traege genug,
// dass genau diese Race auftrat (nur ein PL waehlbar).
export function MultiSelectPicker({
  options,
  value,
  onToggle,
  onClear,
  label,
  placeholder,
  searchPlaceholder,
  clearLabel,
  countLabel,
  noMatchLabel,
}: {
  options: MultiSelectOption[];
  value: string[];
  onToggle: (id: string) => void;
  onClear: () => void;
  label: string;
  placeholder: string;
  searchPlaceholder: string;
  clearLabel: string;
  countLabel: string;
  noMatchLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const valueSet = useMemo(() => new Set(value), [value]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 50);
    return options.filter((o) => o.label.toLowerCase().includes(q)).slice(0, 50);
  }, [options, query]);

  if (options.length === 0) return null;

  const active = value.length > 0;
  const buttonText =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? (options.find((o) => o.value === value[0])?.label ?? `1 ${countLabel}`)
        : `${value.length} ${countLabel}`;

  return (
    <ChipRow label={label}>
      <div ref={ref} className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="px-2.5 py-1 rounded-md text-xs flex items-center gap-1.5 transition-colors"
          style={{
            background: active ? 'var(--primary-soft)' : 'var(--surface-2)',
            color: active ? 'var(--primary)' : 'var(--ink-2)',
            border: `1px solid ${active ? 'var(--primary)' : 'var(--line-soft)'}`,
            fontFamily: 'var(--f-mono)',
          }}
        >
          <span>{buttonText}</span>
          <span style={{ opacity: 0.6 }}>▾</span>
        </button>
        {open && (
          <div
            className="absolute left-0 top-full mt-1 z-20 w-64 rounded shadow-lg"
            style={{ background: 'var(--surface)', border: '1px solid var(--line-soft)' }}
          >
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full text-xs px-2 py-1.5 outline-none rounded-t"
              style={{
                background: 'var(--surface-2)',
                borderBottom: '1px solid var(--line-soft)',
                color: 'var(--ink)',
              }}
            />
            <div className="max-h-60 overflow-y-auto">
              {active && (
                <button
                  type="button"
                  onClick={() => onClear()}
                  className="w-full text-left px-2 py-1.5 text-[11px] border-b"
                  style={{
                    color: 'var(--danger)',
                    borderColor: 'var(--line-soft)',
                    fontFamily: 'var(--f-mono)',
                  }}
                >
                  ✕ {clearLabel}
                </button>
              )}
              {filtered.length === 0 ? (
                <div className="px-2 py-2 text-[11px]" style={{ color: 'var(--ink-3)' }}>
                  {noMatchLabel}
                </div>
              ) : (
                filtered.map((o) => {
                  const checked = valueSet.has(o.value);
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => onToggle(o.value)}
                      className="w-full text-left px-2 py-1.5 text-[11px] hover:bg-[var(--surface-2)] flex items-center gap-2"
                      style={{ color: checked ? 'var(--primary)' : 'var(--ink-2)' }}
                    >
                      <span style={{ width: 12, display: 'inline-block' }}>
                        {checked ? '◉' : '○'}
                      </span>
                      <span className="truncate">{o.label}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </ChipRow>
  );
}
